const WALL  = { bgColor: '#1a1a1a', solid: true }
const FLOOR = { bgColor: '#3a3020', solid: false }

/**
 * Procedurally generate a dungeon using the rooms-and-corridors algorithm.
 * Returns a tiles array compatible with the editor/game format.
 *
 * @param {object} opts
 * @param {number} opts.cols       - Total tile columns
 * @param {number} opts.rows       - Total tile rows
 * @param {number} opts.roomCount  - Max number of rooms to attempt
 * @param {number} opts.minRoom    - Min room dimension (tiles)
 * @param {number} opts.maxRoom    - Max room dimension (tiles)
 */
export const generateDungeon = ({
  cols      = 30,
  rows      = 25,
  roomCount = 10,
  minRoom   = 4,
  maxRoom   = 9,
} = {}) => {
  // Start entirely walled
  const tiles = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ ...WALL }))
  )

  const rooms = []

  const carveRect = (x, y, w, h) => {
    for (let r = y; r < y + h; r++) {
      for (let c = x; c < x + w; c++) {
        if (r >= 0 && r < rows && c >= 0 && c < cols) {
          tiles[r][c] = { ...FLOOR }
        }
      }
    }
  }

  // L-shaped corridor from centre of room A to centre of room B
  const carveCorridor = (x1, y1, x2, y2) => {
    // Randomly choose whether to go horizontal-first or vertical-first
    if (Math.random() < 0.5) {
      // Horizontal leg then vertical
      const stepX = x1 <= x2 ? 1 : -1
      for (let c = x1; c !== x2 + stepX; c += stepX) {
        if (y1 >= 0 && y1 < rows && c >= 0 && c < cols) tiles[y1][c] = { ...FLOOR }
      }
      const stepY = y1 <= y2 ? 1 : -1
      for (let r = y1; r !== y2 + stepY; r += stepY) {
        if (r >= 0 && r < rows && x2 >= 0 && x2 < cols) tiles[r][x2] = { ...FLOOR }
      }
    } else {
      // Vertical leg then horizontal
      const stepY = y1 <= y2 ? 1 : -1
      for (let r = y1; r !== y2 + stepY; r += stepY) {
        if (r >= 0 && r < rows && x1 >= 0 && x1 < cols) tiles[r][x1] = { ...FLOOR }
      }
      const stepX = x1 <= x2 ? 1 : -1
      for (let c = x1; c !== x2 + stepX; c += stepX) {
        if (y2 >= 0 && y2 < rows && c >= 0 && c < cols) tiles[y2][c] = { ...FLOOR }
      }
    }
  }

  // Place rooms, retrying up to 10× per room to avoid overlap
  for (let attempt = 0; attempt < roomCount * 10 && rooms.length < roomCount; attempt++) {
    const w = minRoom + Math.floor(Math.random() * (maxRoom - minRoom + 1))
    const h = minRoom + Math.floor(Math.random() * (maxRoom - minRoom + 1))
    const x = 1 + Math.floor(Math.random() * (cols - w - 2))
    const y = 1 + Math.floor(Math.random() * (rows - h - 2))

    // Reject if it overlaps an existing room (1-tile buffer keeps walls intact)
    const overlaps = rooms.some(
      (r) =>
        x < r.x + r.w + 1 &&
        x + w + 1 > r.x &&
        y < r.y + r.h + 1 &&
        y + h + 1 > r.y
    )
    if (!overlaps) {
      rooms.push({ x, y, w, h })
      carveRect(x, y, w, h)
    }
  }

  // Connect each room to the next via a corridor (ensures full connectivity)
  for (let i = 1; i < rooms.length; i++) {
    const a = rooms[i - 1]
    const b = rooms[i]
    carveCorridor(
      a.x + Math.floor(a.w / 2), a.y + Math.floor(a.h / 2),
      b.x + Math.floor(b.w / 2), b.y + Math.floor(b.h / 2),
    )
  }

  return tiles
}

/** Returns a blank tile grid of the given dimensions */
export const blankTiles = (rows, cols) =>
  Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({})))
